import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Eye, EyeOff, RefreshCw, AlertCircle, CheckCircle, Info } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Helpers ────────────────────────────────────────────────────────────────────
const normalizePhone = (raw = '') => {
  const digits = raw.replace(/\D/g, '');
  const c = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (c.length === 10) return `${c.slice(0, 3)}-${c.slice(3, 6)}-${c.slice(6)}`;
  return raw;
};
const isValidPhone = (p = '') => {
  const d = p.replace(/\D/g, '');
  const c = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
  return c.length === 10;
};
const formatSSN = (raw = '') => {
  const d = raw.replace(/\D/g, '').slice(0, 9);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
};
const generateShortcode = (name = '') => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '';
  if (words.length === 1) return words[0].slice(0, 4).toUpperCase();
  return words.map(w => w[0]).join('').toUpperCase();
};

// ── Type config ────────────────────────────────────────────────────────────────
const TYPE_OPTIONS = [
  { key: 'is_merchant', label: 'Merchant',  active: 'bg-blue-600 text-white border-blue-600',   idle: 'bg-white text-blue-700 border-blue-300 hover:bg-blue-50' },
  { key: 'is_broker',   label: 'Broker',    active: 'bg-purple-600 text-white border-purple-600', idle: 'bg-white text-purple-700 border-purple-300 hover:bg-purple-50' },
  { key: 'is_funder',   label: 'Funder',    active: 'bg-green-600 text-white border-green-600',   idle: 'bg-white text-green-700 border-green-300 hover:bg-green-50' },
  { key: 'is_investor', label: 'Investor',  active: 'bg-orange-600 text-white border-orange-600', idle: 'bg-white text-orange-700 border-orange-300 hover:bg-orange-50' },
  { key: 'is_iso',      label: 'ISO',       active: 'bg-teal-600 text-white border-teal-600',     idle: 'bg-white text-teal-700 border-teal-300 hover:bg-teal-50' },
  { key: 'is_referrer', label: 'Referrer',  active: 'bg-emerald-600 text-white border-emerald-600', idle: 'bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50' },
  { key: 'is_lawyer',   label: 'Lawyer',    active: 'bg-slate-700 text-white border-slate-700',   idle: 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50' },
];

// Shared fields for Broker/Funder/Investor/ISO/Lawyer (everything except Merchant)
const NON_MERCHANT_KEYS = ['is_broker', 'is_funder', 'is_investor', 'is_iso', 'is_lawyer', 'is_referrer'];
const hasNonMerchant = (fd) => NON_MERCHANT_KEYS.some(k => fd[k]);

// ── Masked input ──────────────────────────────────────────────────────────────
function MaskedInput({ id, label, value, onChange, placeholder, testId, helpText }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <div className="relative mt-1">
        <Input
          id={id}
          data-testid={testId}
          type={show ? 'text' : 'password'}
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          className="pr-10"
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setShow(v => !v)}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600"
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {helpText && <p className="text-xs text-slate-500 mt-1">{helpText}</p>}
    </div>
  );
}

// ── Section header ─────────────────────────────────────────────────────────────
function SectionHeader({ title, subtitle }) {
  return (
    <div className="border-b pb-2 mb-3">
      <h3 className="font-semibold text-slate-800">{title}</h3>
      {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
    </div>
  );
}

// ── Main ContactForm ──────────────────────────────────────────────────────────
const ContactForm = ({
  formData,
  setFormData,
  onSubmit,
  onCancel,
  editingContact,
  sensitiveData = {},
  onSensitiveDataChange,
  getAuthHeaders,
}) => {
  const [errors, setErrors] = useState({});
  const [shortcodeStatus, setShortcodeStatus] = useState(null); // null | 'checking' | 'ok' | 'taken'
  const [shortcodeManuallyEdited, setShortcodeManuallyEdited] = useState(false);
  const [showSSN, setShowSSN] = useState(false);
  const checkTimer = useRef(null);
  // 10/19 (Josh): an ISO names the Referrer who brought it in, if any — options = contacts flagged Referrer.
  const [referrers, setReferrers] = useState(null);
  useEffect(() => {
    if (!formData.is_iso || referrers !== null) return;
    (async () => {
      try {
        const res = await fetch(`${API}/contacts?type=Referrer`, { headers: getAuthHeaders ? getAuthHeaders() : {} });
        const data = await res.json();
        setReferrers(Array.isArray(data) ? data : []);
      } catch {
        setReferrers([]);
      }
    })();
  }, [formData.is_iso, referrers, getAuthHeaders]);

  // ── Shortcode auto-gen when company name changes ─────────────────────────
  // 08/12 (Josh): NEW contacts only. Editing an existing contact must NEVER
  // silently regenerate its shortcode (it was overwriting e.g. H2DE1 -> HDI
  // on dialog open) — the stored shortcode is the source of truth and only
  // changes when the operator types a new one deliberately.
  useEffect(() => {
    if (editingContact) return;
    if (!shortcodeManuallyEdited && formData.company_name) {
      const gen = generateShortcode(formData.company_name);
      setFormData(prev => ({ ...prev, shortcode: gen }));
      setShortcodeStatus(null);
    }
  }, [formData.company_name]); // eslint-disable-line

  const checkShortcode = useCallback(async (code) => {
    if (!code) { setShortcodeStatus(null); return; }
    setShortcodeStatus('checking');
    try {
      const params = new URLSearchParams({ code });
      if (editingContact?.id) params.append('exclude_id', editingContact.id);
      const res = await fetch(`${API}/contacts/check-shortcode?${params}`, {
        headers: getAuthHeaders ? getAuthHeaders() : {},
      });
      const data = await res.json();
      setShortcodeStatus(data.available ? 'ok' : 'taken');
      if (!data.available) {
        setErrors(prev => ({ ...prev, shortcode: `Shortcode already taken by "${data.taken_by}" — please edit manually` }));
      } else {
        setErrors(prev => { const n = { ...prev }; delete n.shortcode; return n; });
      }
    } catch {
      setShortcodeStatus(null);
    }
  }, [editingContact, getAuthHeaders]);

  const handleShortcodeBlur = () => {
    if (formData.shortcode) checkShortcode(formData.shortcode);
  };

  const handleChange = (field, value) => {
    const v = typeof value === 'string' && (value === 'true' || value === 'false') ? value === 'true' : value;
    setFormData(prev => ({ ...prev, [field]: v }));
    if (errors[field]) setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  // 08/10 (Josh): "same address as billing"-style checkbox — owner home
  // address mirrors the business address while checked (and stays in sync
  // as the business fields are edited). Unchecking keeps the values but
  // makes them editable again.
  const [ownerSameAsBusiness, setOwnerSameAsBusiness] = useState(false);
  useEffect(() => {
    // Pre-check when editing a contact whose addresses already match.
    if (editingContact && (editingContact.business_address || '').trim()) {
      const same = ['address', 'city', 'state', 'zip'].every(
        (k) => (editingContact[`business_${k}`] || '').trim().toLowerCase()
            === (editingContact[`owner_${k}`] || '').trim().toLowerCase()
      );
      setOwnerSameAsBusiness(same);
    } else {
      setOwnerSameAsBusiness(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingContact?.id]);
  useEffect(() => {
    if (!ownerSameAsBusiness) return;
    setFormData(prev => ({
      ...prev,
      owner_address: prev.business_address || '',
      owner_city: prev.business_city || '',
      owner_state: prev.business_state || '',
      owner_zip: prev.business_zip || '',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerSameAsBusiness, formData.business_address, formData.business_city,
      formData.business_state, formData.business_zip]);

  const handleShortcodeChange = (val) => {
    const up = val.toUpperCase();
    setShortcodeManuallyEdited(true);
    setFormData(prev => ({ ...prev, shortcode: up }));
    setShortcodeStatus(null);
    clearTimeout(checkTimer.current);
    checkTimer.current = setTimeout(() => checkShortcode(up), 600);
  };

  const handleTypeToggle = (key) => {
    setFormData(prev => ({ ...prev, [key]: !prev[key] }));
    setErrors(prev => { const n = { ...prev }; delete n.types; return n; });
  };

  const handlePhoneChange = (raw) => {
    setFormData(prev => ({ ...prev, phone: normalizePhone(raw) }));
  };
  const handlePhoneBlur = () => {
    if (formData.phone && !isValidPhone(formData.phone)) {
      setErrors(prev => ({ ...prev, phone: 'Phone must be 10 digits (e.g. 555-123-4567)' }));
    } else {
      setErrors(prev => { const n = { ...prev }; delete n.phone; return n; });
    }
  };

  const handleSensitiveChange = (field, value) => {
    if (onSensitiveDataChange) onSensitiveDataChange({ ...sensitiveData, [field]: value });
  };

  const validate = () => {
    const errs = {};
    if (!formData.is_person) {
      const typeKeys = TYPE_OPTIONS.map(t => t.key);
      if (!typeKeys.some(k => formData[k])) errs.types = 'Select at least one contact type.';
    }
    if (!formData.company_name?.trim()) errs.company_name = formData.is_person ? 'Name is required.' : 'Company name is required.';
    if (!formData.shortcode?.trim()) errs.shortcode = 'Shortcode is required.';
    if (shortcodeStatus === 'taken') errs.shortcode = 'Shortcode is already taken — please choose another.';
    if (hasNonMerchant(formData) && formData.phone && !isValidPhone(formData.phone)) {
      errs.phone = 'Phone must be 10 digits.';
    }
    return errs;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    onSubmit(e);
  };

  const isPerson = formData.is_person === true;
  const isMerchant = !isPerson && formData.is_merchant;
  const isNonMerchant = !isPerson && hasNonMerchant(formData);
  const handleHomeAddress = (k, v) => setFormData(prev => ({ ...prev, home_address: { ...(prev.home_address || {}), [k]: v } }));
  // years-in-business is DERIVED from Business Start Date — never stored.
  const yearsInBusiness = (() => {
    if (!formData.business_start_date) return null;
    const start = new Date(formData.business_start_date);
    if (Number.isNaN(start.getTime())) return null;
    const y = (Date.now() - start.getTime()) / (365.25 * 24 * 3600 * 1000);
    return y >= 0 ? Math.floor(y * 10) / 10 : null;
  })();

  return (
    <form onSubmit={handleFormSubmit} className="space-y-6 max-h-[75vh] overflow-y-auto pr-2 pb-2">

      {/* ── Record Type ─────────────────────────────────── */}
      <div>
        <SectionHeader title="Record Type" subtitle={editingContact ? 'Record type is fixed after creation.' : 'A business links to its owners; a person can own or guarantee multiple businesses.'} />
        <div className="flex gap-2">
          <button type="button" disabled={!!editingContact} onClick={() => handleChange('is_person', false)} data-testid="record-type-business"
            className={`px-4 py-1.5 rounded-full border text-sm font-medium transition-all ${!isPerson ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'} ${editingContact ? 'opacity-60 cursor-not-allowed' : ''}`}>
            Business
          </button>
          <button type="button" disabled={!!editingContact} onClick={() => handleChange('is_person', true)} data-testid="record-type-person"
            className={`px-4 py-1.5 rounded-full border text-sm font-medium transition-all ${isPerson ? 'bg-rose-600 text-white border-rose-600' : 'bg-white text-rose-700 border-rose-300 hover:bg-rose-50'} ${editingContact ? 'opacity-60 cursor-not-allowed' : ''}`}>
            Person
          </button>
        </div>
      </div>

      {/* ── Contact Types (businesses only) ─────────────── */}
      {!isPerson && (
      <div>
        <SectionHeader title="Contact Type(s)" subtitle="Select one or more — a contact can have multiple roles." />
        <div className="flex flex-wrap gap-2">
          {TYPE_OPTIONS.map(({ key, label, active, idle }) => (
            <button
              key={key}
              type="button"
              onClick={() => handleTypeToggle(key)}
              data-testid={`type-toggle-${key}`}
              className={`px-4 py-1.5 rounded-full border text-sm font-medium transition-all ${formData[key] ? active : idle}`}
            >
              {label}
            </button>
          ))}
        </div>
        {errors.types && <p className="text-xs text-red-600 mt-2 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.types}</p>}
      </div>
      )}

      {/* ── Basic Info ──────────────────────────────────── */}
      <div>
        <SectionHeader title="Basic Information" />
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label htmlFor="company_name">{isPerson ? 'Full Name' : 'Company Name'} <span className="text-red-500">*</span></Label>
            <Input
              id="company_name"
              data-testid="company-name-input"
              value={formData.company_name || ''}
              onChange={e => handleChange('company_name', e.target.value)}
              placeholder={isPerson ? 'e.g. John Smith' : 'e.g. Power Funding Solutions'}
              className={errors.company_name ? 'border-red-400' : ''}
            />
            {errors.company_name && <p className="text-xs text-red-600 mt-1">{errors.company_name}</p>}
          </div>

          {/* Shortcode */}
          <div>
            <Label htmlFor="shortcode" className="flex items-center gap-1">
              Shortcode <span className="text-red-500">*</span>
              <span className="text-xs text-slate-400 font-normal">(auto-generated, editable)</span>
            </Label>
            <div className="relative mt-1">
              <Input
                id="shortcode"
                data-testid="contact-shortcode-input"
                value={formData.shortcode || ''}
                onChange={e => handleShortcodeChange(e.target.value)}
                onBlur={handleShortcodeBlur}
                placeholder="e.g. PFS"
                maxLength={10}
                className={`pr-8 font-mono uppercase ${
                  shortcodeStatus === 'taken' || errors.shortcode
                    ? 'border-red-400 bg-red-50'
                    : shortcodeStatus === 'ok'
                    ? 'border-green-400'
                    : ''
                }`}
              />
              <span className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
                {shortcodeStatus === 'checking' && <RefreshCw className="h-3.5 w-3.5 text-slate-400 animate-spin" />}
                {shortcodeStatus === 'ok' && <CheckCircle className="h-3.5 w-3.5 text-green-500" />}
                {shortcodeStatus === 'taken' && <AlertCircle className="h-3.5 w-3.5 text-red-500" />}
              </span>
            </div>
            {errors.shortcode
              ? <p className="text-xs text-red-600 mt-1 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.shortcode}</p>
              : <p className="text-xs text-slate-400 mt-1">Auto-generated from company name · always uppercase</p>}
          </div>

          {!isPerson && (
          <div>
            <Label htmlFor="dba">DBA (Doing Business As)</Label>
            <Input
              id="dba"
              value={formData.dba || ''}
              onChange={e => handleChange('dba', e.target.value)}
              placeholder="Trade name if different"
            />
          </div>
          )}
        </div>
      </div>

      {/* ── Person Details ──────────────────────────────── */}
      {isPerson && (
        <div>
          <SectionHeader title="Person Details" subtitle="Stable identity — shared across every business this person owns or guarantees." />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="phone">Cell Phone</Label>
              <Input id="phone" data-testid="person-cell-input" value={formData.phone || ''} onChange={e => handlePhoneChange(e.target.value)} onBlur={handlePhoneBlur} placeholder="555-123-4567" className={errors.phone ? 'border-red-400' : ''} />
              {errors.phone && <p className="text-xs text-red-600 mt-1">{errors.phone}</p>}
            </div>
            <div>
              <Label htmlFor="email">Personal Email</Label>
              <Input id="email" type="email" data-testid="person-email-input" value={formData.email || ''} onChange={e => handleChange('email', e.target.value)} placeholder="name@email.com" />
            </div>
            <div>
              <Label htmlFor="dob">Date of Birth</Label>
              <Input id="dob" type="date" data-testid="person-dob-input" value={formData.dob || ''} onChange={e => handleChange('dob', e.target.value)} />
            </div>
            <div className="col-span-2 pt-1">
              <p className="text-sm font-medium text-slate-500 border-b pb-1">Home Address</p>
            </div>
            <div className="col-span-2">
              <Label>Street</Label>
              <Input data-testid="person-home-street1" value={formData.home_address?.street1 || ''} onChange={e => handleHomeAddress('street1', e.target.value)} />
            </div>
            <div className="col-span-2">
              <Label>Street 2</Label>
              <Input value={formData.home_address?.street2 || ''} onChange={e => handleHomeAddress('street2', e.target.value)} placeholder="Apt / Suite (optional)" />
            </div>
            <div>
              <Label>City</Label>
              <Input data-testid="person-home-city" value={formData.home_address?.city || ''} onChange={e => handleHomeAddress('city', e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>State</Label><Input value={formData.home_address?.state || ''} onChange={e => handleHomeAddress('state', e.target.value)} /></div>
              <div><Label>ZIP</Label><Input value={formData.home_address?.zip || ''} onChange={e => handleHomeAddress('zip', e.target.value)} maxLength={10} /></div>
            </div>
          </div>
        </div>
      )}

      {/* ── Contact Details (non-Merchant) ──────────────── */}
      {isNonMerchant && (
        <div>
          <SectionHeader title="Contact Details" />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Contact Person</Label>
              <Input
                id="name"
                data-testid="contact-name-input"
                value={formData.name || ''}
                onChange={e => handleChange('name', e.target.value)}
                placeholder="e.g. Jane Smith"
              />
            </div>
            <div>
              <Label htmlFor="owner_name">Owner Name</Label>
              <Input
                id="owner_name"
                value={formData.owner_name || ''}
                onChange={e => handleChange('owner_name', e.target.value)}
                placeholder="Legal owner name"
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone {isNonMerchant && <span className="text-red-500">*</span>}</Label>
              <Input
                id="phone"
                data-testid="contact-phone-input"
                value={formData.phone || ''}
                onChange={e => handlePhoneChange(e.target.value)}
                onBlur={handlePhoneBlur}
                placeholder="555-123-4567"
                className={errors.phone ? 'border-red-400' : ''}
              />
              {errors.phone && <p className="text-xs text-red-600 mt-1">{errors.phone}</p>}
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="contact-email-input"
                value={formData.email || ''}
                onChange={e => handleChange('email', e.target.value)}
                placeholder="contact@company.com"
              />
            </div>
          </div>

          {/* Additional Emails — used as CC list when emailing this contact (e.g.
              the default-package email auto-CCs the lawyer's secondary addresses). */}
          <div className="mt-4">
            <Label className="text-sm font-medium">
              Additional Emails <span className="text-xs text-gray-500">(auto-CC'd on outgoing emails)</span>
            </Label>
            <div className="space-y-2 mt-2">
              {(formData.emails || []).map((entry, idx) => (
                <div key={idx} className="flex gap-2 items-center" data-testid={`additional-email-row-${idx}`}>
                  <Input
                    type="email"
                    value={entry.email || ''}
                    onChange={e => {
                      const next = [...(formData.emails || [])];
                      next[idx] = { ...next[idx], email: e.target.value };
                      handleChange('emails', next);
                    }}
                    placeholder="paralegal@firm.com"
                    data-testid={`additional-email-input-${idx}`}
                  />
                  <Input
                    type="text"
                    value={entry.type || 'Business'}
                    onChange={e => {
                      const next = [...(formData.emails || [])];
                      next[idx] = { ...next[idx], type: e.target.value };
                      handleChange('emails', next);
                    }}
                    placeholder="Label (e.g. Paralegal)"
                    className="max-w-[180px]"
                    data-testid={`additional-email-type-${idx}`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const next = (formData.emails || []).filter((_, i) => i !== idx);
                      handleChange('emails', next);
                    }}
                    className="text-red-600 hover:text-red-700"
                    data-testid={`additional-email-remove-${idx}`}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleChange('emails', [...(formData.emails || []), { email: '', type: 'Business' }])}
                data-testid="additional-email-add-btn"
              >
                + Add Email
              </Button>
            </div>
          </div>

          {/* Lawyer-specific defaults — only visible when "Lawyer" is selected. */}
          {formData.is_lawyer && (
            <div className="mt-4 p-3 rounded-md border border-slate-200 bg-slate-50 space-y-3">
              <div>
                <Label className="text-sm font-medium">
                  Legal Fee % <span className="text-xs text-gray-500">(canonical contingency rate)</span>
                </Label>
                <div className="flex gap-2 items-center mt-2 max-w-xs">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={formData.legal_fee_pct ?? ''}
                    onChange={e => handleChange('legal_fee_pct', e.target.value === '' ? null : parseFloat(e.target.value))}
                    placeholder="25"
                    data-testid="lawyer-legal-fee-pct-input"
                  />
                  <span className="text-sm text-slate-600">%</span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Pre-fills the "Contingency Rate" on the Default/Recovery tab whenever a deal is assigned to this lawyer (by name match) or when this is the global default lawyer.
                </p>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-500">
                  Legacy Default Contingency Rate <span className="text-[10px] text-gray-400">(fallback)</span>
                </Label>
                <div className="flex gap-2 items-center mt-2 max-w-xs">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={formData.default_contingency_rate ?? ''}
                    onChange={e => handleChange('default_contingency_rate', e.target.value === '' ? null : parseFloat(e.target.value))}
                    placeholder="25"
                    data-testid="lawyer-default-contingency-input"
                  />
                  <span className="text-sm text-slate-600">%</span>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Used only when Legal Fee % is blank.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Address (non-Merchant) ───────────────────────── */}
      {isNonMerchant && (
        <div>
          <SectionHeader title="Business Address" />
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="business_address">Street Address</Label>
              <Input
                id="business_address"
                data-testid="business-address-input"
                value={formData.business_address || ''}
                onChange={e => handleChange('business_address', e.target.value)}
                placeholder="123 Main St, Suite 200"
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="business_street2">Street 2</Label>
              <Input id="business_street2" value={formData.business_street2 || ''} onChange={e => handleChange('business_street2', e.target.value)} placeholder="Suite / Unit (optional)" />
            </div>
            <div>
              <Label htmlFor="business_city">City</Label>
              <Input id="business_city" data-testid="business-city-input" value={formData.business_city || ''} onChange={e => handleChange('business_city', e.target.value)} placeholder="City" />
            </div>
            <div>
              <Label htmlFor="business_state">State</Label>
              <Input id="business_state" data-testid="business-state-input" value={formData.business_state || ''} onChange={e => handleChange('business_state', e.target.value)} placeholder="State" />
            </div>
            <div>
              <Label htmlFor="business_zip">ZIP Code</Label>
              <Input id="business_zip" data-testid="business-zip-input" value={formData.business_zip || ''} onChange={e => handleChange('business_zip', e.target.value)} placeholder="ZIP" maxLength={10} />
            </div>
          </div>
        </div>
      )}

      {/* ── Merchant Section ────────────────────────────── */}
      {isMerchant && (
        <div>
          <SectionHeader title="Merchant Information" />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Contact Name (Person)</Label>
              <Input id="name" value={formData.name || ''} onChange={e => handleChange('name', e.target.value)} placeholder="e.g., John Smith" />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone" data-testid="contact-phone-input" value={formData.phone || ''}
                onChange={e => handlePhoneChange(e.target.value)} onBlur={handlePhoneBlur}
                placeholder="555-123-4567" className={errors.phone ? 'border-red-400' : ''}
              />
              {errors.phone && <p className="text-xs text-red-600 mt-1">{errors.phone}</p>}
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="contact-email-input" value={formData.email || ''} onChange={e => handleChange('email', e.target.value)} placeholder="contact@company.com" />
            </div>
            <div>
              <Label htmlFor="industry">Industry</Label>
              <Input id="industry" value={formData.industry || ''} onChange={e => handleChange('industry', e.target.value)} />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <MaskedInput
                id="ein_m"
                label="EIN / Tax ID"
                value={formData.ein}
                onChange={v => handleChange('ein', v)}
                placeholder="XX-XXXXXXX"
                testId="merchant-ein-input"
                helpText="Format: XX-XXXXXXX"
              />
            </div>
            <div>
              <Label htmlFor="state_of_incorporation">State of Incorporation</Label>
              <Input id="state_of_incorporation" data-testid="state-of-incorporation-input" value={formData.state_of_incorporation || ''} onChange={e => handleChange('state_of_incorporation', e.target.value.toUpperCase())} placeholder="e.g. NY" maxLength={2} />
              <p className="text-xs text-slate-500 mt-1">Printed on contracts ("State of Incorporation"). Two-letter code.</p>
            </div>
            <div>
              <Label htmlFor="business_start_date">Business Start Date</Label>
              <Input id="business_start_date" type="date" data-testid="business-start-date-input" value={formData.business_start_date || ''} onChange={e => handleChange('business_start_date', e.target.value)} />
              {yearsInBusiness != null && (
                <p className="text-xs text-slate-500 mt-1" data-testid="years-in-business-derived">{yearsInBusiness} years in business (derived)</p>
              )}
            </div>

            {/* Business Address */}
            <div className="col-span-2 pt-1">
              <p className="text-sm font-medium text-slate-500 border-b pb-1">Business Address</p>
            </div>
            <div className="col-span-2">
              <Label htmlFor="business_address_m">Street Address</Label>
              <Input id="business_address_m" data-testid="business-address-input" value={formData.business_address || ''} onChange={e => handleChange('business_address', e.target.value)} />
            </div>
            <div className="col-span-2">
              <Label htmlFor="business_street2_m">Street 2</Label>
              <Input id="business_street2_m" data-testid="business-street2-input" value={formData.business_street2 || ''} onChange={e => handleChange('business_street2', e.target.value)} placeholder="Suite / Unit (optional)" />
            </div>
            <div>
              <Label>City</Label>
              <Input data-testid="business-city-input" value={formData.business_city || ''} onChange={e => handleChange('business_city', e.target.value)} />
            </div>
            <div>
              <Label>State</Label>
              <Input data-testid="business-state-input" value={formData.business_state || ''} onChange={e => handleChange('business_state', e.target.value)} />
            </div>
            <div>
              <Label>ZIP</Label>
              <Input data-testid="business-zip-input" value={formData.business_zip || ''} onChange={e => handleChange('business_zip', e.target.value)} maxLength={10} />
            </div>

            {/* Owner info */}
            <div className="col-span-2 pt-1">
              <p className="text-sm font-medium text-slate-500 border-b pb-1">Owner Information</p>
            </div>
            <div>
              <Label htmlFor="owner_name">Owner Name</Label>
              <Input id="owner_name" value={formData.owner_name || ''} onChange={e => handleChange('owner_name', e.target.value)} />
            </div>
            <div>
              <Label htmlFor="owner_dob">Date of Birth</Label>
              <Input id="owner_dob" type="date" value={formData.owner_dob || ''} onChange={e => handleChange('owner_dob', e.target.value)} />
            </div>

            {/* SSN */}
            <div className="col-span-2">
              <Label>Social Security Number</Label>
              <div className="relative">
                <Input
                  data-testid="owner-ssn-input"
                  type={showSSN ? 'text' : 'password'}
                  value={formData.owner_ssn || ''}
                  onChange={e => handleChange('owner_ssn', formatSSN(e.target.value))}
                  placeholder="XXX-XX-XXXX"
                  maxLength={11}
                  className="pr-10"
                />
                <button type="button" tabIndex={-1} onClick={() => setShowSSN(v => !v)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600" data-testid="ssn-toggle-btn">
                  {showSSN ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Auto-formats as XXX-XX-XXXX</p>
            </div>

            {/* Owner Home Address */}
            <div className="col-span-2 pt-1">
              <p className="text-sm font-medium text-slate-500 border-b pb-1">Owner Home Address</p>
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <Checkbox
                id="owner_same_as_business"
                checked={ownerSameAsBusiness}
                onCheckedChange={(v) => setOwnerSameAsBusiness(!!v)}
                data-testid="owner-same-as-business-checkbox"
              />
              <Label htmlFor="owner_same_as_business" className="text-sm font-normal cursor-pointer">
                Same as business address
              </Label>
            </div>
            <div className="col-span-2">
              <Label>Street Address</Label>
              <Input data-testid="owner-address-input" value={formData.owner_address || ''} onChange={e => handleChange('owner_address', e.target.value)} disabled={ownerSameAsBusiness} className={ownerSameAsBusiness ? 'bg-slate-50 text-slate-500' : ''} />
            </div>
            <div>
              <Label>City</Label>
              <Input data-testid="owner-city-input" value={formData.owner_city || ''} onChange={e => handleChange('owner_city', e.target.value)} disabled={ownerSameAsBusiness} className={ownerSameAsBusiness ? 'bg-slate-50 text-slate-500' : ''} />
            </div>
            <div>
              <Label>State</Label>
              <Input data-testid="owner-state-input" value={formData.owner_state || ''} onChange={e => handleChange('owner_state', e.target.value)} disabled={ownerSameAsBusiness} className={ownerSameAsBusiness ? 'bg-slate-50 text-slate-500' : ''} />
            </div>
            <div>
              <Label>ZIP</Label>
              <Input data-testid="owner-zip-input" value={formData.owner_zip || ''} onChange={e => handleChange('owner_zip', e.target.value)} maxLength={10} disabled={ownerSameAsBusiness} className={ownerSameAsBusiness ? 'bg-slate-50 text-slate-500' : ''} />
            </div>

            <div className="col-span-2">
              <Label htmlFor="business_description">Business Description</Label>
              <Textarea
                id="business_description"
                value={formData.business_description || ''}
                onChange={e => handleChange('business_description', e.target.value)}
                rows={2}
                placeholder="Brief description of the business"
                className="resize-none"
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Broker Settings ─────────────────────────────── */}
      {formData.is_broker && (
        <div>
          <SectionHeader title="Broker Commission Settings" subtitle="Default split applied automatically when this broker is assigned to a deal." />
          <div>
            <Label htmlFor="broker_commission_rate">Commission Split Rate (%)</Label>
            <Input
              id="broker_commission_rate"
              data-testid="broker-commission-rate-input"
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={formData.broker_commission_rate || ''}
              onChange={e => handleChange('broker_commission_rate', e.target.value === '' ? null : parseFloat(e.target.value))}
              placeholder="e.g. 35 for 35% of ISO commission"
              className="max-w-xs"
            />
            <p className="text-xs text-slate-500 mt-1">This percentage of the ISO commission will go to the broker at deal level. EPO adjustments are applied separately.</p>
          </div>
        </div>
      )}

      {/* ── ISO Commission Display ───────────────────────── */}
      {formData.is_iso && (
        <div>
          <SectionHeader title="ISO Commission" />
          <div className="flex items-start gap-3 bg-teal-50 border border-teal-200 rounded-lg px-4 py-3">
            <Info className="h-4 w-4 text-teal-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-teal-800">Commission Rate: 100%</p>
              <p className="text-xs text-teal-700 mt-0.5">ISO retains full commission. Subject to clawbacks and EPO adjustments at deal level.</p>
            </div>
          </div>
          <div className="mt-3">
            <Label htmlFor="referrer_contact_id">Who referred this ISO, if anyone?</Label>
            <select
              id="referrer_contact_id"
              data-testid="iso-referrer-select"
              className="mt-1 block w-full max-w-sm rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              value={formData.referrer_contact_id || ''}
              onChange={e => handleChange('referrer_contact_id', e.target.value || null)}
            >
              <option value="">No referrer</option>
              {(referrers || []).map(r => (
                <option key={r.id} value={r.id}>{r.company_name || r.name}</option>
              ))}
              {formData.referrer_contact_id && referrers && !referrers.some(r => r.id === formData.referrer_contact_id) && (
                <option value={formData.referrer_contact_id}>{formData.referrer_name || formData.referrer_contact_id}</option>
              )}
            </select>
            <p className="text-xs text-slate-500 mt-1">
              Options are contacts with the Referrer type (Habib, Mordi, Jay, Nissim…). Add one under Contacts first if missing.
              {referrers && referrers.length === 0 && ' No Referrer contacts exist yet.'}
            </p>
          </div>
        </div>
      )}

      {/* ── Referrer Settings (10/20) ────────────────────── */}
      {formData.is_referrer && (
        <div>
          <SectionHeader title="Referral Split" subtitle="Share of the 2% ISO pool on deals brought in by the ISOs this person referred. Leave blank until agreed — nothing is computed from a blank." />
          <div>
            <Label htmlFor="referral_split_pct">Referral split (% of the 2% pool)</Label>
            <Input
              id="referral_split_pct"
              data-testid="referral-split-pct-input"
              type="number" step="0.01" min="0" max="100"
              value={formData.referral_split_pct ?? ''}
              onChange={e => handleChange('referral_split_pct', e.target.value === '' ? null : parseFloat(e.target.value))}
              placeholder="e.g. 25 for a quarter of the pool"
              className="max-w-xs"
            />
            <p className="text-xs text-slate-500 mt-1">Used by Syndications → Payouts → Referral splits (read-only figures; payouts are still recorded by hand).</p>
          </div>
        </div>
      )}

      {/* ── Investor Settings ────────────────────────────── */}
      {formData.is_investor && (
        <div>
          <SectionHeader title="Investor / Syndication Settings" />
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Checkbox
                id="collect_fees"
                checked={formData.collect_fees === true}
                onCheckedChange={v => handleChange('collect_fees', v === true)}
              />
              <Label htmlFor="collect_fees">Origination entitlement (shares the deal's origination fees)</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="collects_bounce_fees"
                checked={formData.collects_bounce_fees === true}
                onCheckedChange={v => handleChange('collects_bounce_fees', v === true)}
              />
              <Label htmlFor="collects_bounce_fees">Collects bounce fees</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="collects_default_fees"
                checked={formData.collects_default_fees === true}
                onCheckedChange={v => handleChange('collects_default_fees', v === true)}
              />
              <Label htmlFor="collects_default_fees">Collects default fees</Label>
            </div>
            <div>
              <Label htmlFor="management_fee_rate">Management Fee Rate (%)</Label>
              <Input
                id="management_fee_rate"
                data-testid="management-fee-rate-input"
                type="number" step="0.01" min="0" max="100"
                value={formData.management_fee_rate || ''}
                onChange={e => handleChange('management_fee_rate', e.target.value === '' ? null : parseFloat(e.target.value))}
                placeholder="e.g. 2.0 for 2%"
                className="max-w-xs"
              />
              <p className="text-xs text-slate-500 mt-1">Deducted from syndicator's net distribution per payment (goes to house)</p>
            </div>
          </div>
        </div>
      )}

      {/* ── Financial Info (non-Merchant) ──────────────── */}
      {isNonMerchant && (
        <div>
          <SectionHeader title="Financial Information" subtitle="EIN and banking details. All fields are masked by default." />
          <div className="grid grid-cols-2 gap-4">
            {!isMerchant && (
            <div className="col-span-2 sm:col-span-1">
              <MaskedInput
                id="ein"
                label="EIN / Tax ID"
                value={formData.ein}
                onChange={v => handleChange('ein', v)}
                placeholder="XX-XXXXXXX"
                testId="ein-input"
                helpText="Format: XX-XXXXXXX"
              />
            </div>
            )}
            <div className="col-span-2 sm:col-span-1">
              <MaskedInput
                id="bank_account"
                label="Bank Account Number"
                value={sensitiveData.bank_account_full || ''}
                onChange={v => handleSensitiveChange('bank_account_full', v)}
                placeholder="Enter account number"
                testId="bank-account-input"
              />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <MaskedInput
                id="routing_number"
                label="Routing Number"
                value={sensitiveData.routing_number_full || ''}
                onChange={v => handleSensitiveChange('routing_number_full', v)}
                placeholder="9-digit routing number"
                testId="routing-number-input"
              />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <Label htmlFor="bank_name">Bank Name</Label>
              <Input
                id="bank_name"
                value={sensitiveData.bank_name || ''}
                onChange={e => handleSensitiveChange('bank_name', e.target.value)}
                placeholder="e.g. Chase Bank"
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Notes ───────────────────────────────────────── */}
      <div>
        <SectionHeader title="Notes" />
        <Textarea
          id="additional_info"
          data-testid="additional-info-input"
          value={formData.additional_info || ''}
          onChange={e => handleChange('additional_info', e.target.value)}
          rows={3}
          placeholder="Any notes about this contact..."
          className="resize-none"
        />
      </div>

      {/* ── Form Actions ─────────────────────────────────── */}
      <div className="flex justify-end gap-3 pt-4 border-t sticky bottom-0 bg-white py-3">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" data-testid="contact-submit-btn">
          {editingContact ? 'Save Changes' : isPerson ? 'Create Person' : 'Create Record'}
        </Button>
      </div>
    </form>
  );
};

export default ContactForm;
